import React, { memo, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import dayjs from 'dayjs';
import styled from 'styled-components';
import { Checkbox, DatePicker, Input, Select } from 'antd';

import fontService, { FontWeight } from 'services/FontService';
import { tablet } from 'services/ui';
import { SeniorityOptions, WorkModeOptions } from 'context';
import { CareerIcon } from 'atoms/CareerIcon/CareerIcon';
import { FormFieldset } from 'organisms/ResumeForm';
import type { ICareerProfileFormValues } from 'organisms/CareerProfileForm/types';

export interface IProfileBasicsBlock {
  values: ICareerProfileFormValues;
  onChange: (name: string, value: string | boolean) => void;
  /** Фото профиля — аватар и «Zmień zdjęcie» (0072). */
  photo?: ReactNode;
}

/**
 * Language-neutral profile facts (spec 0018 §3.1): identity + job-search
 * preferences that live nowhere else in the system.
 */
export const ProfileBasicsBlock = memo(({ values, onChange, photo }: IProfileBasicsBlock) => {
  const { t } = useTranslation();

  return (
    <FormFieldset groupTitle={t('careerProfile.basics.title')}>
      {photo}
      <Grid>
        <Field>
          <Label>{t('careerProfile.basics.firstName')}</Label>
          <Input
            value={values.firstName}
            maxLength={100}
            onChange={({ target: { value } }) => onChange('firstName', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.lastName')}</Label>
          <Input
            value={values.lastName}
            maxLength={100}
            onChange={({ target: { value } }) => onChange('lastName', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.email')}</Label>
          <Input
            value={values.email}
            onChange={({ target: { value } }) => onChange('email', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.phone')}</Label>
          <Input
            value={values.phone}
            onChange={({ target: { value } }) => onChange('phone', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.city')}</Label>
          <Input
            value={values.city}
            onChange={({ target: { value } }) => onChange('city', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.country')}</Label>
          <Input
            value={values.country}
            onChange={({ target: { value } }) => onChange('country', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.seniority')}</Label>
          <Select
            style={{ width: '100%' }}
            allowClear
            clearIcon={<CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />}
            value={values.seniority || undefined}
            options={SeniorityOptions}
            placeholder={t('careerProfile.basics.seniorityPlaceholder')}
            onChange={(value) => onChange('seniority', value ?? '')}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.workMode')}</Label>
          <Select
            style={{ width: '100%' }}
            allowClear
            clearIcon={<CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />}
            value={values.workMode || undefined}
            options={WorkModeOptions}
            placeholder={t('careerProfile.basics.workModePlaceholder')}
            onChange={(value) => onChange('workMode', value ?? '')}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.desiredPosition')}</Label>
          <Input
            value={values.desiredPosition}
            aria-describedby="cv-desired-position-hint"
            onChange={({ target: { value } }) => onChange('desiredPosition', value)}
          />
          {/* 0072: это НЕ заголовок CV — по нему подбираются вакансии. */}
          <Hint id="cv-desired-position-hint">{t('careerProfile.basics.desiredPositionHint')}</Hint>
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.desiredSalary')}</Label>
          <Input
            value={values.desiredSalary}
            onChange={({ target: { value } }) => onChange('desiredSalary', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.workPermit')}</Label>
          <Input
            value={values.workPermit}
            onChange={({ target: { value } }) => onChange('workPermit', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.availableFrom')}</Label>
          <DatePicker
            style={{ width: '100%' }}
            clearIcon={<CareerIcon name="close" size={14} title={t('careerCenter.clearField')} />}
            value={values.availableFrom ? dayjs(values.availableFrom) : null}
            onChange={(_, dateString) =>
              onChange('availableFrom', Array.isArray(dateString) ? dateString[0] : dateString)
            }
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.github')}</Label>
          <Input
            value={values.githubUrl}
            onChange={({ target: { value } }) => onChange('githubUrl', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.linkedin')}</Label>
          <Input
            value={values.linkedinUrl}
            onChange={({ target: { value } }) => onChange('linkedinUrl', value)}
          />
        </Field>
        <Field>
          <Label>{t('careerProfile.basics.portfolio')}</Label>
          <Input
            value={values.portfolioUrl}
            onChange={({ target: { value } }) => onChange('portfolioUrl', value)}
          />
        </Field>
      </Grid>

      <CheckboxRow>
        <Checkbox
          checked={values.relocationReady}
          onChange={({ target: { checked } }) => onChange('relocationReady', checked)}>
          {t('careerProfile.basics.relocationReady')}
        </Checkbox>
      </CheckboxRow>
    </FormFieldset>
  );
});

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;

  ${tablet} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.span`
  margin-bottom: 4px;
  ${fontService.text({ size: 14, lineHeight: 145, weight: FontWeight.medium })}
`;

const Hint = styled.span`
  margin-top: 4px;
  color: var(--career-text-2, #62666d);
  ${fontService.text({ size: 14, lineHeight: 143 })}
`;

const CheckboxRow = styled.div`
  margin-top: 20px;
`;
